import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { getCompanies, getBatches, createBatch, uploadFile } from '../services/api';
import { Company, Batch } from '../types/api';

export default function Batches() {
  const navigate = useNavigate();
  const [companies, setCompanies] = useState<Company[]>([]);
  const [selectedCompany, setSelectedCompany] = useState<number | null>(null);
  const [batches, setBatches] = useState<Batch[]>([]);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [newBatch, setNewBatch] = useState({ si_ref: '', pi_ref: '', company_id: null as number | null });
  const [uploading, setUploading] = useState(false);
  const [dragActive, setDragActive] = useState(false);

  // Company search state for modal
  const [companySearch, setCompanySearch] = useState('');
  const [showCompanyDropdown, setShowCompanyDropdown] = useState(false);
  const companySearchRef = useRef<HTMLDivElement>(null);

  // Company filter search state
  const [filterSearch, setFilterSearch] = useState('');
  const [showFilterDropdown, setShowFilterDropdown] = useState(false);
  const filterSearchRef = useRef<HTMLDivElement>(null);

  // Date filter state
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');

  useEffect(() => {
    loadCompanies();
    loadBatches(); // Load all batches initially
  }, []);

  useEffect(() => {
    loadBatches();
  }, [selectedCompany, dateFrom, dateTo]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (companySearchRef.current && !companySearchRef.current.contains(event.target as Node)) {
        setShowCompanyDropdown(false);
      }
      if (filterSearchRef.current && !filterSearchRef.current.contains(event.target as Node)) {
        setShowFilterDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const loadCompanies = async () => {
    try {
      const data = await getCompanies();
      setCompanies(data);
    } catch (error) {
      console.error('Failed to load companies:', error);
    }
  };

  const loadBatches = async () => {
    setLoading(true);
    try {
      const params: {
        company_id?: number;
        date_from?: string;
        date_to?: string;
      } = {};
      if (selectedCompany) params.company_id = selectedCompany;
      if (dateFrom) params.date_from = dateFrom;
      if (dateTo) params.date_to = dateTo;
      const data = await getBatches(params);
      setBatches(data);
    } catch (error) {
      console.error('Failed to load batches:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateBatch = async () => {
    if (!newBatch.company_id) {
      alert('Please select a company');
      return;
    }
    try {
      const result = await createBatch({
        company_id: newBatch.company_id,
        si_ref: newBatch.si_ref || undefined,
        pi_ref: newBatch.pi_ref || undefined,
      });
      setShowCreateModal(false);
      setNewBatch({ si_ref: '', pi_ref: '', company_id: null });
      setCompanySearch('');
      loadBatches();
      navigate(`/batches/${result.id}`);
    } catch (error) {
      console.error('Failed to create batch:', error);
    }
  };

  const handleDrop = async (e: React.DragEvent, batchId: number) => {
    e.preventDefault();
    setDragActive(false);

    const files = Array.from(e.dataTransfer.files);
    if (files.length === 0) return;

    setUploading(true);
    try {
      for (const file of files) {
        await uploadFile(file, batchId);
      }
      loadBatches();
    } catch (error) {
      console.error('Failed to upload files:', error);
    } finally {
      setUploading(false);
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'pending': return 'bg-gray-100 text-gray-800';
      case 'processing': return 'bg-blue-100 text-blue-800';
      case 'completed': return 'bg-green-100 text-green-800';
      case 'failed': return 'bg-red-100 text-red-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getProgressColor = (progress: number, hasErrors: boolean) => {
    if (hasErrors) return 'bg-red-500';
    if (progress === 100) return 'bg-green-500';
    return 'bg-blue-500';
  };

  // Filter companies based on search (for create modal)
  const filteredCompanies = companies.filter(company =>
    company.company_name.toLowerCase().includes(companySearch.toLowerCase())
  );

  // Filter companies based on search (for filter dropdown)
  const filterSearchCompanies = companies.filter(company =>
    company.company_name.toLowerCase().includes(filterSearch.toLowerCase())
  );

  const selectedCompanyForBatch = companies.find(c => c.id === newBatch.company_id);
  const selectedFilterCompany = companies.find(c => c.id === selectedCompany);

  return (
    <div className="p-6">
      {/* Header */}
      <div className="mb-6">
        <div className="flex items-center justify-between">
          <h1 className="text-2xl font-bold text-gray-900">Document Batches</h1>
          <button
            onClick={() => setShowCreateModal(true)}
            className="flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
          >
            <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
            </svg>
            New Batch
          </button>
        </div>

        {/* Filters */}
        <div className="mt-4 flex flex-wrap items-end gap-4">
          {/* Company Filter */}
          <div ref={filterSearchRef}>
            <label className="block text-xs font-medium text-gray-500 mb-1">Company</label>
            <div className="relative w-64">
              <input
                type="text"
                value={selectedFilterCompany ? selectedFilterCompany.company_name : filterSearch}
                onChange={(e) => {
                  setFilterSearch(e.target.value);
                  setSelectedCompany(null);
                  setShowFilterDropdown(true);
                }}
                onFocus={() => setShowFilterDropdown(true)}
                placeholder="All Companies"
                className="w-full px-3 py-2 bg-white border border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
              />
              {selectedFilterCompany && (
                <button
                  type="button"
                  onClick={() => {
                    setSelectedCompany(null);
                    setFilterSearch('');
                  }}
                  className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              )}

              {/* Filter Dropdown */}
              {showFilterDropdown && !selectedFilterCompany && (
                <div className="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-lg shadow-lg max-h-60 overflow-y-auto">
                  <button
                    type="button"
                    onClick={() => {
                      setSelectedCompany(null);
                      setFilterSearch('');
                      setShowFilterDropdown(false);
                    }}
                    className="w-full text-left px-3 py-2 hover:bg-blue-50 focus:bg-blue-50 focus:outline-none font-medium text-gray-600"
                  >
                    All Companies
                  </button>
                  {filterSearchCompanies.map((company) => (
                    <button
                      key={company.id}
                      type="button"
                      onClick={() => {
                        setSelectedCompany(company.id);
                        setFilterSearch('');
                        setShowFilterDropdown(false);
                      }}
                      className="w-full text-left px-3 py-2 hover:bg-blue-50 focus:bg-blue-50 focus:outline-none"
                    >
                      <span className="text-gray-900">{company.company_name}</span>
                    </button>
                  ))}
                  {filterSearchCompanies.length === 0 && filterSearch && (
                    <div className="px-3 py-2 text-gray-500 text-sm">No companies found</div>
                  )}
                </div>
              )}
            </div>
          </div>

          {/* Date From */}
          <div>
            <label className="block text-xs font-medium text-gray-500 mb-1">Date From</label>
            <input
              type="date"
              value={dateFrom}
              onChange={(e) => setDateFrom(e.target.value)}
              className="px-3 py-2 bg-white border border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
            />
          </div>

          {/* Date To */}
          <div>
            <label className="block text-xs font-medium text-gray-500 mb-1">Date To</label>
            <input
              type="date"
              value={dateTo}
              onChange={(e) => setDateTo(e.target.value)}
              className="px-3 py-2 bg-white border border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
            />
          </div>

          {/* Clear Filters */}
          {(selectedCompany || dateFrom || dateTo) && (
            <button
              onClick={() => {
                setSelectedCompany(null);
                setFilterSearch('');
                setDateFrom('');
                setDateTo('');
              }}
              className="px-3 py-2 text-sm text-gray-600 hover:text-gray-800"
            >
              Clear Filters
            </button>
          )}
        </div>
      </div>

      {/* Batches Grid */}
      {loading ? (
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
        </div>
      ) : batches.length === 0 ? (
        <div className="text-center py-12 bg-white rounded-lg shadow">
          <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
          </svg>
          <h3 className="mt-2 text-sm font-medium text-gray-900">No batches</h3>
          <p className="mt-1 text-sm text-gray-500">Get started by creating a new batch.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {batches.map((batch) => (
            <div
              key={batch.id}
              className={`bg-white rounded-lg shadow hover:shadow-md transition-shadow cursor-pointer ${
                dragActive ? 'ring-2 ring-blue-500' : ''
              }`}
              onClick={() => navigate(`/batches/${batch.id}`)}
              onDragOver={(e) => { e.preventDefault(); setDragActive(true); }}
              onDragLeave={() => setDragActive(false)}
              onDrop={(e) => handleDrop(e, batch.id)}
            >
              <div className="p-4">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-lg font-semibold text-gray-900">
                    Batch #{batch.id}
                  </span>
                  <span className={`px-2 py-1 text-xs font-medium rounded-full ${getStatusColor(batch.status)}`}>
                    {batch.status}
                  </span>
                </div>

                {/* Company name */}
                <p className="text-sm font-medium text-blue-600 mb-1">
                  {batch.company_name}
                </p>

                <div className="text-sm text-gray-500 space-y-1">
                  {batch.si_ref && <p>SI Ref: {batch.si_ref}</p>}
                  {batch.pi_ref && <p>PI Ref: {batch.pi_ref}</p>}
                  <p>{batch.files_count || 0} files</p>
                </div>

                {/* Progress bar */}
                {(batch.progress !== undefined && batch.progress > 0) && (
                  <div className="mt-3">
                    <div className="flex items-center justify-between text-xs text-gray-500 mb-1">
                      <span>Progress</span>
                      <span>{batch.progress}%</span>
                    </div>
                    <div className="w-full h-2 bg-gray-200 rounded-full overflow-hidden">
                      <div
                        className={`h-full transition-all duration-500 ${getProgressColor(batch.progress, false)}`}
                        style={{ width: `${batch.progress}%` }}
                      />
                    </div>
                    {batch.completed_files !== undefined && (
                      <p className="text-xs text-gray-400 mt-1">
                        {batch.completed_files} / {batch.total_files} files processed
                      </p>
                    )}
                  </div>
                )}

                <p className="text-xs text-gray-400 mt-2">
                  Created: {new Date(batch.created_at).toLocaleDateString()}
                </p>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Upload indicator */}
      {uploading && (
        <div className="fixed bottom-4 right-4 bg-blue-600 text-white px-4 py-2 rounded-lg shadow-lg flex items-center">
          <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
          Uploading files...
        </div>
      )}

      {/* Create Batch Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-md p-6">
            <h2 className="text-xl font-bold mb-4">Create New Batch</h2>

            <div className="space-y-4">
              {/* Company Search */}
              <div ref={companySearchRef}>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Company <span className="text-red-500">*</span>
                </label>
                <div className="relative">
                  <input
                    type="text"
                    value={selectedCompanyForBatch ? selectedCompanyForBatch.company_name : companySearch}
                    onChange={(e) => {
                      setCompanySearch(e.target.value);
                      setNewBatch({ ...newBatch, company_id: null });
                      setShowCompanyDropdown(true);
                    }}
                    onFocus={() => setShowCompanyDropdown(true)}
                    placeholder="Search for a company..."
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                  {selectedCompanyForBatch && (
                    <button
                      type="button"
                      onClick={() => {
                        setNewBatch({ ...newBatch, company_id: null });
                        setCompanySearch('');
                      }}
                      className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                    >
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                      </svg>
                    </button>
                  )}

                  {/* Dropdown */}
                  {showCompanyDropdown && !selectedCompanyForBatch && (
                    <div className="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-lg shadow-lg max-h-60 overflow-y-auto">
                      {filteredCompanies.length === 0 ? (
                        <div className="px-3 py-2 text-gray-500 text-sm">No companies found</div>
                      ) : (
                        filteredCompanies.map((company) => (
                          <button
                            key={company.id}
                            type="button"
                            onClick={() => {
                              setNewBatch({ ...newBatch, company_id: company.id });
                              setCompanySearch('');
                              setShowCompanyDropdown(false);
                            }}
                            className="w-full text-left px-3 py-2 hover:bg-blue-50 focus:bg-blue-50 focus:outline-none"
                          >
                            <span className="font-medium text-gray-900">{company.company_name}</span>
                          </button>
                        ))
                      )}
                    </div>
                  )}
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Sales Invoice Reference (SI)
                </label>
                <input
                  type="text"
                  value={newBatch.si_ref}
                  onChange={(e) => setNewBatch({ ...newBatch, si_ref: e.target.value })}
                  placeholder="e.g., SI001"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Purchase Invoice Reference (PI)
                </label>
                <input
                  type="text"
                  value={newBatch.pi_ref}
                  onChange={(e) => setNewBatch({ ...newBatch, pi_ref: e.target.value })}
                  placeholder="e.g., PI001"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
            </div>

            <div className="flex justify-end space-x-3 mt-6">
              <button
                onClick={() => {
                  setShowCreateModal(false);
                  setNewBatch({ si_ref: '', pi_ref: '', company_id: null });
                  setCompanySearch('');
                }}
                className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleCreateBatch}
                disabled={!newBatch.company_id}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Create Batch
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
